import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { hasOverlayAbove } from '@/lib/overlayStack';
import { useT } from '@/lib/i18n';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogTitle = DialogPrimitive.Title;
export const DialogDescription = DialogPrimitive.Description;

interface DialogContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  /** A dialog must be labelled; pass a title even when it is visually hidden. */
  children: ReactNode;
  /** Quick Add sits high on screen rather than centred, closer to the eye. */
  position?: 'center' | 'top';
}

export function DialogContent({
  className,
  children,
  position = 'center',
  onEscapeKeyDown,
  ...props
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
      <DialogPrimitive.Content
        onEscapeKeyDown={(event) => {
          // Something is stacked on top — a lightbox, say. Escape belongs to it,
          // and it will close itself; closing this as well would take the whole
          // task away when the user only wanted to shrink an image.
          if (hasOverlayAbove()) {
            event.preventDefault();
            return;
          }
          onEscapeKeyDown?.(event);
        }}
        className={cn(
          'fixed left-1/2 z-50 w-full max-w-lg -translate-x-1/2 rounded-lg',
          'border border-border bg-surface-raised shadow-xl shadow-black/10',
          'focus:outline-none',
          position === 'top' ? 'top-[18vh]' : 'top-1/2 -translate-y-1/2',
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogCloseButton() {
  const t = useT();
  return (
    <DialogPrimitive.Close
      className={cn(
        'absolute right-3 top-3 rounded p-1 text-content-subtle',
        'transition-surface hover:bg-surface-hover hover:text-content',
      )}
      aria-label={t('common.close')}
    >
      <X className="h-4 w-4" />
    </DialogPrimitive.Close>
  );
}

/** Visually hidden but announced — for dialogs whose title is implied visually. */
export function DialogSrTitle({ children }: { children: ReactNode }) {
  return <DialogPrimitive.Title className="sr-only">{children}</DialogPrimitive.Title>;
}
